import { useCallback, useSyncExternalStore } from 'react';

/* ── Tiny in-memory toast store (framework-agnostic) ── */

let nextId = 1;
let toasts = [];
const listeners = new Set();

function emit() {
    listeners.forEach((fn) => fn());
}

function subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

function getSnapshot() {
    return toasts;
}

function addToast(variant, title, description, durationMs = 5000) {
    const id = nextId++;
    const toast = { id, variant, title, description, createdAt: Date.now() };
    toasts = [...toasts, toast];
    emit();
    if (durationMs > 0) {
        setTimeout(() => removeToast(id), durationMs);
    }
    return id;
}

function removeToast(id) {
    toasts = toasts.filter((t) => t.id !== id);
    emit();
}

/**
 * React hook that returns a stable toast API.
 *
 * Usage:
 * ```js
 * const toast = useToast();
 * toast.success('Saved', 'Your changes have been saved.');
 * toast.error('Oops', 'Something went wrong.');
 * toast.info('Heads up', 'You have a new message.');
 * toast.warning('Careful', 'This action cannot be undone.');
 * ```
 */
export function useToast() {
    const items = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

    const success = useCallback(
        (title, description, ms) => addToast('success', title, description, ms),
        [],
    );
    const error = useCallback(
        (title, description, ms) => addToast('error', title, description, ms),
        [],
    );
    const info = useCallback(
        (title, description, ms) => addToast('info', title, description, ms),
        [],
    );
    const warning = useCallback(
        (title, description, ms) => addToast('warning', title, description, ms),
        [],
    );
    const dismiss = useCallback((id) => removeToast(id), []);

    return { items, success, error, info, warning, dismiss };
}

export { removeToast, addToast };

export default useToast;
